import { useState } from "react";
import { RotateCcw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { clearAppData, resetUiPreferences } from "@/lib/localAppData";

export const TroubleshootingSection = () => {
  const [isClearing, setIsClearing] = useState(false);

  const clearBrowserCache = async () => {
    setIsClearing(true);
    try {
      // Clear service worker caches
      if ("caches" in window) {
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map((name) => caches.delete(name)));
      }

      // Clear IndexedDB
      if ("indexedDB" in window) {
        const dbs = await indexedDB.databases();
        dbs.forEach((db) => {
          if (db.name) {
            indexedDB.deleteDatabase(db.name);
          }
        });
      }

      toast.success("Browser cache cleared successfully");
    } catch (error) {
      console.error("Error clearing browser cache:", error);
      toast.error("Failed to clear browser cache");
    } finally {
      setIsClearing(false);
    }
  };

  const clearAllLocalStorage = () => {
    try {
      localStorage.clear();
      toast.success("All localStorage cleared successfully");
    } catch (error) {
      console.error("Error clearing localStorage:", error);
      toast.error("Failed to clear localStorage");
    }
  };

  const handleClearAppData = () => {
    try {
      const cleared = clearAppData();
      if (cleared > 0) {
        toast.success(`Cleared ${cleared} cached app data items`);
      } else {
        toast.info("No cached app data found");
      }
    } catch (error) {
      console.error("Error clearing app data:", error);
      toast.error("Failed to clear app data");
    }
  };

  const handleResetUiPreferences = () => {
    try {
      const cleared = resetUiPreferences();
      if (cleared > 0) {
        toast.success("UI preferences reset. Reloading...");
        setTimeout(() => window.location.reload(), 800);
      } else {
        toast.info("No saved UI preferences found");
      }
    } catch (error) {
      console.error("Error resetting UI preferences:", error);
      toast.error("Failed to reset UI preferences");
    }
  };

  return (
    <Card className="p-6">
      <div className="space-y-6">
        <div>
          <h2 className="text-xl font-semibold text-[#222B45] mb-2">Browser Cache & Storage</h2>
          <p className="text-sm text-gray-600 mb-4">
            If you're experiencing issues with the web app, clearing your browser cache and local
            storage may help resolve them.
          </p>
        </div>

        <div className="space-y-3">
          <div>
            <h3 className="text-sm font-semibold text-[#222B45] mb-2">Browser Cache</h3>
            <Button
              onClick={clearBrowserCache}
              disabled={isClearing}
              variant="outline"
              className="w-full sm:w-auto"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              {isClearing ? "Clearing..." : "Clear Browser Cache"}
            </Button>
            <p className="text-xs text-gray-500 mt-2">Clears service worker caches and IndexedDB</p>
          </div>

          <div>
            <h3 className="text-sm font-semibold text-[#222B45] mb-2">App Data</h3>
            <div className="space-y-2">
              <Button
                onClick={handleClearAppData}
                variant="outline"
                className="w-full sm:w-auto"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Clear App Data
              </Button>
              <p className="text-xs text-gray-500">
                Clears cached transcripts, genie list, voices and models — they reload automatically
              </p>
            </div>
          </div>

          <div>
            <h3 className="text-sm font-semibold text-[#222B45] mb-2">UI Preferences</h3>
            <Button
              onClick={handleResetUiPreferences}
              variant="outline"
              className="w-full sm:w-auto"
            >
              <RotateCcw className="w-4 h-4 mr-2" />
              Reset UI Preferences
            </Button>
            <p className="text-xs text-gray-500 mt-2">
              Restores default layout and views, and shows dismissed tips again
            </p>
          </div>

          <div>
            <h3 className="text-sm font-semibold text-[#222B45] mb-2">All Local Storage</h3>
            <Button
              onClick={clearAllLocalStorage}
              variant="destructive"
              className="w-full sm:w-auto"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Clear All Storage
            </Button>
            <p className="text-xs text-gray-500 mt-2">
              Clears all locally stored data (you may need to log in again)
            </p>
          </div>
        </div>
      </div>
    </Card>
  );
};
